import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RemediationWorkflowPanel = ({ remediations, violations, loading, onRefresh }) => {
  const [expandedRemediation, setExpandedRemediation] = useState(null);

  const getPriorityColor = (priority) => {
    const colors = {
      immediate: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-success'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  const getPriorityBg = (priority) => {
    const backgrounds = {
      immediate: 'bg-error/10',
      high: 'bg-warning/10',
      medium: 'bg-primary/10',
      low: 'bg-success/10'
    };
    return backgrounds?.[priority] || 'bg-muted';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      in_progress: 'text-primary',
      completed: 'text-success',
      failed: 'text-error'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Loading remediation workflows...</p>
      </div>
    );
  }

  if (!remediations || remediations?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="CheckSquare" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
        <p className="text-sm text-muted-foreground mb-4">No remediation workflows available</p>
        <Button variant="outline" size="sm" onClick={onRefresh} iconName="RefreshCw">
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-heading font-semibold text-base text-foreground">
          Remediation Workflows ({remediations?.length})
        </h4>
        <Button variant="outline" size="sm" onClick={onRefresh} iconName="RefreshCw">
          Refresh
        </Button>
      </div>
      <div className="space-y-3">
        {remediations?.map((remediation) => {
          const isExpanded = expandedRemediation === remediation?.id;
          const relatedViolation = violations?.find(v => v?.id === remediation?.violationId);

          return (
            <div key={remediation?.id} className="border border-border rounded-lg overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedRemediation(isExpanded ? null : remediation?.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="CheckSquare" size={18} color="var(--color-primary)" />
                      <h5 className="font-heading font-semibold text-sm text-foreground">
                        {remediation?.actionType?.replace(/_/g, ' ')}
                      </h5>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{remediation?.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        getPriorityBg(remediation?.priority)
                      } ${getPriorityColor(remediation?.priority)}`}>
                        {remediation?.priority?.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full bg-muted ${
                        getStatusColor(remediation?.remediationStatus)
                      }`}>
                        {remediation?.remediationStatus?.replace(/_/g, ' ')}
                      </span>
                      {remediation?.affectedCount && (
                        <span className="text-xs text-muted-foreground">
                          {remediation?.affectedCount} employees affected
                        </span>
                      )}
                    </div>
                  </div>
                  <Icon 
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                    size={20} 
                    color="var(--color-muted-foreground)" 
                  />
                </div>
              </div>
              {isExpanded && (
                <div className="border-t border-border p-4 bg-muted/20">
                  {/* Related Violation */}
                  {relatedViolation && (
                    <div className="mb-4 p-3 bg-card rounded-lg">
                      <p className="text-xs font-medium text-foreground mb-2">Related Violation</p>
                      <p className="text-xs text-muted-foreground">
                        {relatedViolation?.violationType?.replace(/_/g, ' ')} - {relatedViolation?.severity}
                      </p>
                    </div>
                  )}

                  {/* Implementation Steps */}
                  {remediation?.implementationSteps && remediation?.implementationSteps?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-foreground mb-2">Implementation Steps</p>
                      <ol className="list-decimal list-inside space-y-1">
                        {remediation?.implementationSteps?.map((step, index) => (
                          <li key={index} className="text-xs text-muted-foreground">{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Backpay Amount</p>
                      <p className="text-sm font-medium text-warning">
                        K{remediation?.backpayAmount?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Scheduled Date</p>
                      <p className="text-sm font-medium text-foreground">
                        {remediation?.scheduledDate 
                          ? new Date(remediation.scheduledDate)?.toLocaleDateString()
                          : 'Not scheduled'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Assigned To */}
                  {remediation?.assignedTo && (
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                      <p className="text-xs text-muted-foreground">
                        Assigned to: <span className="font-medium text-foreground">{remediation?.assignedTo}</span>
                      </p>
                    </div>
                  )}

                  {/* Notes */}
                  {remediation?.notes && (
                    <div className="p-2 bg-muted/50 rounded mb-4">
                      <p className="text-xs text-muted-foreground italic">{remediation?.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {remediation?.remediationStatus === 'pending' && (
                      <Button variant="primary" size="sm">
                        Start Workflow
                      </Button>
                    )}
                    {remediation?.remediationStatus === 'in_progress' && (
                      <Button variant="success" size="sm">
                        Mark Complete
                      </Button>
                    )}
                    <Button variant="outline" size="sm" iconName="FileText">
                      View Details
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RemediationWorkflowPanel;